import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { CancelIcon, CaretsVerticalIcon, ChevronUpIcon, ChevronDownIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import { getHourOptions, TIME_FORMATS, regexValidation, minuteOptions, ampmOptions, TWELVE_HOUR_FORMAT, TWENTY_FOUR_HOUR_FORMAT, getRegexExpression } from '../utils/TimeHelpers.js';
import Button from './Button.js';
import Card from './Card.js';
import Form from './Form.js';
import Grid from './Grid.js';
import Overlay from './Overlay.js';

var _excluded = ["onUpdate", "format", "error", "errorText", "onError", "id", "value", "placeholder", "data-testid", "disabled", "highlight", "required", "hintText", "label", "className"];
var TimePicker = function TimePicker(_ref) {
  var onUpdate = _ref.onUpdate,
    _ref$format = _ref.format,
    format = _ref$format === void 0 ? 'standard' : _ref$format,
    error = _ref.error,
    errorText = _ref.errorText,
    onError = _ref.onError,
    id = _ref.id,
    value = _ref.value,
    placeholder = _ref.placeholder,
    dataTestId = _ref['data-testid'],
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$highlight = _ref.highlight,
    highlight = _ref$highlight === void 0 ? false : _ref$highlight,
    _ref$required = _ref.required,
    required = _ref$required === void 0 ? false : _ref$required,
    hintText = _ref.hintText,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Time' : _ref$label,
    className = _ref.className,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var isStandardTime = format === 'standard';
  var formatValue = format === 'standard' ? TWELVE_HOUR_FORMAT : TWENTY_FOUR_HOUR_FORMAT;
  var regex = getRegexExpression(isStandardTime);
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    timeValue = _useState2[0],
    setTimeValue = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedTime = _useState4[0],
    setSelectedTime = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isShowingOptions = _useState6[0],
    setIsShowingOptions = _useState6[1];
  var _useState7 = useState(getHourOptions(isStandardTime)),
    _useState8 = _slicedToArray(_useState7, 2),
    isHourOptions = _useState8[0];
    _useState8[1];
  var inputRef = useRef();
  var dropdownButtonRef = useRef();
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isInternalError = _useState10[0],
    setIsInternalError = _useState10[1];
  var externalError = {
    error: error,
    errorText: errorText ? errorText : "Please make sure time entered is in valid format: ".concat(formatValue)
  };
  var internalError = {
    error: isInternalError,
    errorText: "Please make sure time entered is in valid format: ".concat(formatValue)
  };
  var finalClasses = classnames('C-TimePicker', className);
  var cardClasses = classnames('C-TimePicker__Card', {
    isMilitary: !isStandardTime
  });
  if (!TIME_FORMATS.includes(formatValue)) {
    console.error('Hey Canvas User! The TimePicker does not support the format: ' + formatValue);
  }
  useEffect(function () {
    setTimeValue(value);
    //Check of its valid value
    var _regexValidation = regexValidation(value ? value : '', regex, isStandardTime),
      _regexValidation2 = _slicedToArray(_regexValidation, 2),
      error = _regexValidation2[0],
      timeString = _regexValidation2[1];
    if (error && (value === null || value === void 0 ? void 0 : value.length) > 0) {
      setIsInternalError(true);
    } else {
      setTimeValue(timeString);
      setIsInternalError(false);
      var stringArray = timeString === null || timeString === void 0 ? void 0 : timeString.split('');
      var timeObject = (stringArray === null || stringArray === void 0 ? void 0 : stringArray.length) > 0 ? isStandardTime ? {
        hour: "".concat(stringArray[0]).concat(stringArray[1]),
        meridien: "".concat(stringArray[6]).concat(stringArray[7]),
        minute: "".concat(stringArray[3]).concat(stringArray[4])
      } : {
        hour: "".concat(stringArray[0]).concat(stringArray[1]),
        minute: "".concat(stringArray[3]).concat(stringArray[4])
      } : null;
      setSelectedTime(timeObject);
    }
  }, [value]);
  useEffect(function () {
    onError && onError(internalError.error, timeValue);
    var coreValue = timeValue === null || timeValue === void 0 ? void 0 : timeValue.trim();
    if (internalError.error === false && (isStandardTime ? (coreValue === null || coreValue === void 0 ? void 0 : coreValue.length) >= 8 : (coreValue === null || coreValue === void 0 ? void 0 : coreValue.length) >= 5)) {
      onUpdate && onUpdate(id, coreValue);
    }
  }, [internalError.error, timeValue, id]);
  var handleOptionsClose = useCallback(function () {
    var _inputRef$current;
    // close options list no matter what
    setIsShowingOptions(false);
    inputRef === null || inputRef === void 0 ? void 0 : (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
  }, []);
  var handleOnClear = function handleOnClear() {
    setSelectedTime();
    setTimeValue('');
    setIsShowingOptions(false);
    setIsInternalError(false);
  };
  var handleClose = function handleClose(newTime) {
    if (newTime !== null && newTime !== void 0 && newTime.match(regex)) {
      handleOptionsClose();
    }
  };
  var changeEvent = function changeEvent(event) {
    var _event$target;
    var _regexValidation3 = regexValidation((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value, regex, isStandardTime),
      _regexValidation4 = _slicedToArray(_regexValidation3, 2),
      error = _regexValidation4[0],
      timeString = _regexValidation4[1];
    if (error) {
      setIsInternalError(true);
    } else {
      setIsInternalError(false);
    }
    setTimeValue(timeString);
    var stringArray = timeString.split('');
    var timeObject = isStandardTime ? {
      hour: "".concat(stringArray[0]).concat(stringArray[1]),
      meridien: "".concat(stringArray[(stringArray === null || stringArray === void 0 ? void 0 : stringArray.length) - 2]).concat(stringArray[(stringArray === null || stringArray === void 0 ? void 0 : stringArray.length) - 1]),
      minute: "".concat(stringArray[3]).concat(stringArray[4])
    } : {
      hour: "".concat(stringArray[0]).concat(stringArray[1]),
      minute: "".concat(stringArray[3]).concat(stringArray[4])
    };
    setSelectedTime(timeObject);
    handleClose(timeString);
  };
  var handleSetTime = function handleSetTime(time, format) {
    var newTime = isStandardTime ? {
      hour: format === 'hh' ? time : selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.hour,
      meridien: format === 'aa' ? time : selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.meridien,
      minute: format === 'mm' ? time : selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.minute
    } : {
      hour: format === 'hh' ? time : selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.hour,
      minute: format === 'mm' ? time : selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.minute
    };
    var newSelectedTime = "".concat(newTime.hour !== undefined ? newTime.hour : '', ":").concat(newTime.minute !== undefined ? newTime.minute : '', " ").concat(isStandardTime ? newTime.meridien !== undefined ? newTime.meridien : '' : '');
    var _regexValidation5 = regexValidation(newSelectedTime, regex, isStandardTime),
      _regexValidation6 = _slicedToArray(_regexValidation5, 2),
      error = _regexValidation6[0],
      timeString = _regexValidation6[1];
    if (error) {
      setIsInternalError(true);
    } else {
      setIsInternalError(false);
    }
    setTimeValue(timeString);
    setSelectedTime(newTime);
    handleClose(timeString);
  };
  Array.prototype.rotate = function (n) {
    var len = this.length;
    return !(n % len) ? this.slice() : this.map(function (e, i, a) {
      return a[(i + (len + n % len)) % len];
    });
  };
  var scrollDown = function scrollDown(type) {
    var currentScroll = document.querySelector("#".concat(type)).scrollTop;
    document.querySelector("#".concat(type)).scrollTo({
      behavior: 'smooth',
      top: currentScroll + 32
    });
  };
  var scrollUp = function scrollUp(type) {
    var currentScroll = document.querySelector("#".concat(type)).scrollTop;
    if (currentScroll > 0) {
      document.querySelector("#".concat(type)).scrollTo({
        behavior: 'smooth',
        top: currentScroll - 32
      });
    }
  };
  var handleUserKeyPress = function handleUserKeyPress(event) {
    var key = event.key,
      keyCode = event.keyCode;
    if (key === 'Escape' || key === 'Esc' || keyCode === 72) {
      var _dropdownButtonRef$cu;
      dropdownButtonRef === null || dropdownButtonRef === void 0 ? void 0 : (_dropdownButtonRef$cu = dropdownButtonRef.current) === null || _dropdownButtonRef$cu === void 0 ? void 0 : _dropdownButtonRef$cu.focus();
      setIsShowingOptions(false);
    }
  };

  // listen for escape key press when dropdown menu is visible
  useEffect(function () {
    if (isShowingOptions) {
      window.addEventListener('keyup', handleUserKeyPress);
    }
    return function () {
      // returned function will be called on component unmount
      window.removeEventListener('keyup', handleUserKeyPress);
    };
  }, [isShowingOptions]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Overlay, {
    type: "embedded",
    isVisible: isShowingOptions,
    onClick: handleOptionsClose,
    className: '--timepicker'
  }), /*#__PURE__*/React__default.createElement("div", {
    "data-testid": dataTestId,
    className: finalClasses
  }, /*#__PURE__*/React__default.createElement("div", {
    "data-testid": "".concat(dataTestId, "_screenReader"),
    role: "alert",
    "aria-live": "polite",
    "aria-label": "please input a time in format ".concat(formatValue),
    className: "hc-sr-only"
  }), /*#__PURE__*/React__default.createElement("div", {
    className: "C-TimePicker__Input"
  }, /*#__PURE__*/React__default.createElement(Form.Field, _extends({}, otherProps, {
    disabled: disabled,
    error: !isShowingOptions && (internalError.error || externalError.error),
    errorText: !isShowingOptions && externalError.error ? externalError.errorText : internalError.errorText,
    highlight: highlight,
    hintText: hintText && hintText,
    id: id,
    label: label,
    ref: inputRef,
    role: "combobox" //TODO: combobox instructions not valid- remove? or add open event
    ,
    "aria-live": "assertive",
    onChange: changeEvent,
    placeholder: placeholder ? placeholder : formatValue,
    required: required,
    value: timeValue,
    "data-testid": dataTestId ? "".concat(dataTestId, "_formfield") : null
  })), /*#__PURE__*/React__default.createElement("span", {
    className: "C-TimePicker__IconArea"
  }, timeValue && /*#__PURE__*/React__default.createElement(Button, {
    className: "C-TimePicker__NavButton --embeddedInComponent",
    disabled: disabled,
    type: "ghost",
    iconOnly: true,
    "aria-label": "clear",
    size: "dense",
    onClick: function onClick() {
      handleOnClear();
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    size: "sm",
    icon: CancelIcon
  })), /*#__PURE__*/React__default.createElement(Button, {
    ref: dropdownButtonRef,
    iconOnly: true,
    size: 'dense',
    className: "C-TimePicker__NavButton --embeddedInComponent",
    disabled: disabled,
    "aria-expanded": isShowingOptions,
    "data-testid": dataTestId && "".concat(dataTestId, "_").concat(isShowingOptions ? 'closeTimePicker' : 'openTimePicker'),
    "aria-label": isShowingOptions ? 'close time picker' : 'open time picker',
    onClick: function onClick() {
      return setIsShowingOptions(!isShowingOptions);
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: CaretsVerticalIcon,
    size: "md"
  })))), isShowingOptions && !disabled && /*#__PURE__*/React__default.createElement(Card, {
    elevation: 2,
    "data-testid": dataTestId && "".concat(dataTestId, "_dropdown"),
    className: cardClasses
  }, /*#__PURE__*/React__default.createElement(Grid.Container, {
    spacing: "dense"
  }, /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    className: "C-TimePicker__ColumnButton"
  }, /*#__PURE__*/React__default.createElement(Button, {
    iconOnly: true,
    "aria-label": "hour column up arrow",
    type: 'ghost',
    className: '__NavButton --embeddedInComponent',
    onClick: function onClick() {
      scrollUp('hour-column');
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: ChevronUpIcon,
    className: "__IconArea"
  }))), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    className: "C-TimePicker__ColumnButton"
  }, /*#__PURE__*/React__default.createElement(Button, {
    iconOnly: true,
    type: 'ghost',
    "aria-label": "minute column up arrow",
    className: '__NavButton --embeddedInComponent ',
    onClick: function onClick() {
      scrollUp('minutes-column');
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: ChevronUpIcon,
    className: "__IconArea"
  }))), isStandardTime && /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: 4
  }), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    id: "hour-column",
    className: "C-TimePicker__TimeColumn"
  }, isHourOptions.map(function (hour, index) {
    var isSelected = hour === (selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.hour);
    return /*#__PURE__*/React__default.createElement(Button, {
      "aria-label": hour && "hour ".concat(hour),
      "aria-current": isSelected
      // aria-live="polite" TODO: CLEANUP
      ,
      id: "hour-".concat(hour),
      type: 'ghost',
      key: "hour_".concat(index),
      className: "\n                    --embeddedInComponent\n                    ".concat(isSelected ? 'isSelected' : ''),
      onClick: function onClick() {
        handleSetTime(hour, 'hh');
      }
    }, hour);
  })), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    className: "C-TimePicker__TimeColumn",
    id: "minutes-column"
  }, minuteOptions.map(function (minute, index) {
    var isSelected = minute === (selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.minute);
    return /*#__PURE__*/React__default.createElement(Button, {
      id: "minutes-".concat(minute),
      type: 'ghost',
      "aria-current": isSelected,
      "aria-label": minute ? "minute ".concat(minute) : 'hidden',
      key: "minute_".concat(index),
      className: "\n                    --embeddedInComponent\n                    ".concat(isSelected ? 'isSelected' : ''),
      onClick: function onClick() {
        handleSetTime(minute, 'mm');
      }
    }, minute);
  })), isStandardTime && /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: 4,
    "data-testid": dataTestId && "".concat(dataTestId, "-ampm-column"),
    id: "ampm-column"
  }, isStandardTime ? ampmOptions.map(function (aa, index) {
    var isSelected = aa === (selectedTime === null || selectedTime === void 0 ? void 0 : selectedTime.meridien);
    return /*#__PURE__*/React__default.createElement(Button, {
      "aria-label": aa ? aa : 'hidden',
      "aria-current": isSelected,
      type: 'ghost',
      key: "ampm_".concat(index),
      className: "\n                      --embeddedInComponent \n                      ".concat(isSelected ? 'isSelected' : ''),
      onClick: function onClick() {
        handleSetTime(aa, 'aa');
      }
    }, aa);
  }) : null), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    className: "C-TimePicker__ColumnButton"
  }, /*#__PURE__*/React__default.createElement(Button, {
    iconOnly: true,
    type: 'ghost',
    "aria-label": "hour column down arrow",
    className: '--embeddedInComponent hc-pl-md',
    onClick: function onClick() {
      scrollDown('hour-column');
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: ChevronDownIcon,
    className: "__IconArea"
  }))), /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: !isStandardTime ? 6 : 4,
    className: "C-TimePicker__ColumnButton"
  }, /*#__PURE__*/React__default.createElement(Button, {
    iconOnly: true,
    type: 'ghost',
    "aria-label": "minute column down arrow",
    className: '--embeddedInComponent hc-pl-md',
    onClick: function onClick() {
      scrollDown('minutes-column');
    }
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: ChevronDownIcon,
    className: "__IconArea"
  }))), isStandardTime && /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: 4
  })))));
};

export { TimePicker as default };
